import React, { useEffect, useState, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import { apiCall } from "../service/api-service";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView, canViewSensitiveFields } from "../common/permissionUtils";

// API Imports
import { getItemsListApi, bulkDeleteItemsApi, exportItemsApi } from "./item-api";
import { sanitizeInput, sanitizeFileName, validateFileSize } from "../common/security-utils";

// SVG Images
import Search from "../../assets/svg/search.svg";
import Plus from "../../assets/svg/add.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import exportBtn from "../../assets/svg/bl_export_ic.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import DeleteModal from "../common/delete-modal.js";
import ImportFileModal from "../common/importfile-modal.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";

// Persist list UI state (search, pagination) across navigation to a view
// page and back. Uses sessionStorage so it survives refresh but clears when
// the browser tab is closed.
const ITEM_LIST_STATE_KEY = "itemListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(ITEM_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const ItemList = () => {
  const persisted = loadPersistedState();

  const [hoveredIndex, setHoveredIndex] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Item");
  const hasEditPermission = canEdit(rolePermissionList, "Item");
  const hasDeletePermission = canDelete(rolePermissionList, "Item");
  const hasViewPermission = canView(rolePermissionList, "Item");
  const hasSensitiveFieldAccess = canViewSensitiveFields(rolePermissionList, "Item");

  const options = ["SKU", "Item Name", "HSN Code", "Price", ...(hasSensitiveFieldAccess ? ["Purchase Price"] : []), "Current Stock", "Status"];

  const [search, setSearch] = useState(persisted.search ?? "");

  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => Object.fromEntries(options.map((label) => [label, true])));

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  const columnsGridTemplate = visibleOptions.map(() => "1fr").join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  // Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setIsBulkDelete(false);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsBulkDelete(false);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      setSelectedRows([]);
      setAllSelected(false);
      getItemsList();
    }
  };

  const handleBulkDelete = () => {
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete items");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setIsOpenDelete(true);
  };

  // Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // Get Items List
  const [itemsList, setItemsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const getItemsList = async () => {
    setListLoading(true);
    try {
      const response = await getItemsListApi(`?search=${search}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}`);

      const responseData = response?.data || {};
      const rawItems = responseData.items || [];

      if (responseData.success === true) {
        setPageCount(responseData.total_pgs || 0);
        setRecords(responseData.total_rec || 0);
        const formatted = formatItemsData(Array.isArray(rawItems) ? rawItems : []);
        setItemsList(formatted);
      }
      return responseData;
    } catch (err) {
      // Error handled silently
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getItemsList();
    setSelectedRows([]);
    setAllSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, itemsPerPage, itemOffset]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedItemsList = React.useMemo(() => {
    if (!sortColumn) return itemsList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...itemsList].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (sortColumn === "Status") {
        return (aVal === bVal ? 0 : aVal ? 1 : -1) * dir;
      }
      const aStr = (aVal ?? "").toString().toLowerCase();
      const bStr = (bVal ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [itemsList, sortColumn, sortDirection]);

  // When the Item sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setItemOffset(0);
      setItemsPerPage(10);
      sessionStorage.removeItem(ITEM_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        ITEM_LIST_STATE_KEY,
        JSON.stringify({
          search,
          itemOffset,
          itemsPerPage,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, itemOffset, itemsPerPage]);

  const formatItemsData = (rawItems) => {
    return rawItems.map((item) => ({
      id: item.id,
      SKU: item.item_code,
      "Item Name": item.item_name,
      "HSN Code": item.hsn_no,
      Price: item.price,
      "Purchase Price": item.purchase_price,
      "Current Stock": item.available_stock,
      Status: Number(item.status) === 1,
    }));
  };

  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (itemsList.length > 0) {
      setAllSelected(selectedRows.length === itemsList.length);
    }
  }, [selectedRows, itemsList]);

  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(itemsList.map((item) => item.id));
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(true);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Import / Export
  const [showImport, setShowImport] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const downloadBase64Excel = (base64String, filePrefix, fileSizeErrorMessage) => {
    if (!validateFileSize(base64String, 10)) {
      setSnackbarMessage(fileSizeErrorMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return false;
    }

    const fileName = sanitizeFileName(`${filePrefix}_${Date.now()}.xlsx`);
    const binary = atob(base64String);
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiCall("GET", "item_template_download");
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(response.data.base64, "Item_Template", "File size exceeds 10MB limit");
      }
      setSnackbarMessage(response?.data?.message || "Unable to download template");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
    } catch (err) {
      // Error handled silently
    }
  };

  const uploadTicket = async (base64String) => {
    if (!base64String || typeof base64String !== "string") {
      setSnackbarMessage("Invalid file data");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setIsImporting(true);
    try {
      const response = await apiCall("POST", "items_upload", { upload_file: base64String });

      if (response?.data?.success === true) {
        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Item_Upload_Report", "Report file size exceeds 10MB limit");
        }
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(true);
        handleSnackbarShow();
        setShowImport(false);
        getItemsList();
      } else {
        setSnackbarMessage(response?.data?.message || "Upload failed");
        setSnackbarStatus(false);
        handleSnackbarShow();

        if (response?.data?.base64) {
          downloadBase64Excel(response.data.base64, "Item_Upload_Error", "Error file size exceeds 10MB limit");
        }
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setIsImporting(false);
    }
  };

  // Exports whatever the list is currently showing (search term applied).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportItemsApi(`?search=${search}`);
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(response.data.base64, "Item_Report", "File size exceeds 10MB limit");
        setSnackbarMessage(response.data.message || "Excel exported successfully");
        setSnackbarStatus(true);
      } else {
        setSnackbarMessage(response?.data?.message || "Unable to export items");
        setSnackbarStatus(false);
      }
      handleSnackbarShow();
    } catch (err) {
      setSnackbarMessage("Unable to export items");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isOpenDelete && <DeleteModal deleteUrl={isBulkDelete ? null : `item/delete/${deleteId}`} bulkDeleteApi={isBulkDelete ? bulkDeleteItemsApi : null} bulkDeleteIds={isBulkDelete ? selectedRows : null} onSuccess={onSuccessDelete} onCancel={cancelDelete} />}
        <div style={{ marginBottom: "16px" }}>
          <Row className="justify-content-between g-3">
            <Col xs={12} md={4}>
              <div className="fs-14 fw-600 text-dkblue">Items - {records}</div>
            </Col>

            <Col xs={12} md={5} lg={4}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "100px",
                  border: "0.8px solid #e5e5e5",
                  padding: "9px 16px",
                  backgroundColor: "#ffffff",
                  height: "40px",
                }}
              >
                <img src={Search} alt="search" style={{ width: "16px" }} />
                <input
                  type="search"
                  placeholder="Search..."
                  className="no-focus-border"
                  value={search}
                  onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    flex: 1,
                    backgroundColor: "transparent",
                  }}
                />
              </div>
            </Col>

            <Col xs={12} md={6} lg={4} className="d-flex justify-content-end">
              {selectedRows.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "0 20px",
                    borderRadius: "50px",
                    borderBottom: "3px solid #B31717",
                    background: "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                    height: "40px",
                    width: "auto",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                  onClick={handleBulkDelete}
                >
                  <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
                </div>
              )}
              {selectedRows.length === 0 && hasCreatePermission && (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      borderBottom: "3px solid #1723C2",
                      background: "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                      height: "40px",
                      width: "fit-content",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/item/create")}
                  >
                    <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                    <div className="fs-15 fw-600 text-white">Create Item</div>
                  </div>
                </div>
              )}

              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginLeft: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 16px",
                    borderRadius: "100px",
                    border: "1px solid #1723C2",
                    height: "40px",
                    width: "fit-content",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowImport(true)}
                >
                  <img src={ExcelImportIC} style={{ width: "16px" }} alt="Import" />
                  <div className="fs-15 fw-600" style={{ color: "#3D4AF5" }}>
                    Import
                  </div>
                </div>
              </div>

              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginLeft: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 16px",
                    borderRadius: "100px",
                    border: "1px solid #1723C2",
                    height: "40px",
                    width: "fit-content",
                    cursor: isExporting ? "not-allowed" : "pointer",
                    opacity: isExporting ? 0.6 : 1,
                  }}
                  onClick={isExporting ? undefined : handleExport}
                >
                  <img src={exportBtn} style={{ width: "16px" }} alt="Export" />
                  <div className="fs-15 fw-600" style={{ color: "#3D4AF5" }}>
                    {isExporting ? "Exporting..." : "Export"}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <ImportFileModal
          type="items"
          open={showImport}
          onClose={() => setShowImport(false)}
          onExport={downloadTemplate}
          onImport={(base64) => uploadTicket(base64)}
          isImporting={isImporting}
        />

        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                    }}
                  >
                    <div
                      style={{
                        width: "72px",
                        padding: "10px 24px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                      {visibleOptions.map((label) => (
                        <div
                          key={label}
                          onClick={() => handleSortClick(label)}
                          title={`Sort by ${label}`}
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRight: "1px solid #ebebeb",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                            <div
                              className="fs-12 fw-600 text-ltgrey"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label.toUpperCase()}
                            </div>
                            <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                              {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                            </span>
                          </div>
                          <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                        </div>
                      ))}
                    </div>

                    <div
                      onClick={() => setShowModal(true)}
                      style={{
                        width: "72px",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img src={Customize} alt="customize" />
                    </div>
                  </div>

                  {/* Data Rows */}
                  {listLoading ? (
                    <div style={{ width: "100%", padding: "40px 0" }}>
                      <Loader height="120px" />
                    </div>
                  ) : sortedItemsList.length === 0 ? (
                    <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                      No items found
                    </div>
                  ) : (
                    sortedItemsList.map((item, rowIndex) => (
                    <div
                      key={item.id}
                      onMouseEnter={() => setHoveredIndex(rowIndex)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        borderBottom: "1px solid #ebebeb",
                        backgroundColor: "#ffffff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          width: "72px",
                          padding: "10px 24px",
                          flexShrink: 0,
                          borderRight: "1px solid #ebebeb",
                        }}
                      >
                        <input type="checkbox" checked={selectedRows.includes(item.id)} onChange={() => toggleRow(item.id)} disabled={!anyColumnSelected} />
                      </div>
                      <div
                        style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                        onClick={() => {
                          if (hasViewPermission) {
                            navigate("/item/view", { state: { id: item.id } });
                          }
                        }}
                      >
                        {visibleOptions.map((label) => (
                          <div
                            key={label}
                            title={item[label]}
                            className="text-dkblue fs-14 fw-400"
                            style={{
                              minWidth: 0,
                              padding: "10px 16px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              borderRight: "1px solid #ebebeb",
                            }}
                          >
                            {label === "Status" ? (
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "100px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  backgroundColor: item[label] ? "#e6f4ea" : "#fce8e6",
                                  color: item[label] ? "#1e7e34" : "#d93025",
                                }}
                              >
                                {item[label] ? "Active" : "Inactive"}
                              </span>
                            ) : label === "Price" || label === "Purchase Price" ? (
                              `₹ ${item[label] ?? "0.00"}`
                            ) : (
                              item[label] ?? "-"
                            )}
                          </div>
                        ))}
                      </div>
                      <div style={{ width: "72px", flexShrink: 0 }} />
                      {hoveredIndex === rowIndex && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            right: "12px",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {hasEditPermission && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/item/create", { state: { id: item.id } });
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  border: "0.6px solid #E5E5E5",
                                  cursor: "pointer",
                                  justifyContent: "center",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "100px",
                                  background: "#FFFFFF",
                                }}
                                title="Edit Item"
                              >
                                <EditRow />
                              </div>
                            )}
                            {hasEditPermission && hasDeletePermission && <span style={{ marginLeft: "16px" }}></span>}
                            {hasDeletePermission && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(item.id);
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  border: "0.6px solid #E5E5E5",
                                  cursor: "pointer",
                                  justifyContent: "center",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "100px",
                                  background: "#FFFFFF",
                                }}
                                title="Delete Item"
                              >
                                <DeleteRow />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    ))
                  )}

                  {showModal && (
                    <div
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#fff",
                          padding: "24px",
                          borderRadius: "12px",
                          boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                          width: "300px",
                          maxHeight: "80vh",
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            marginBottom: "16px",
                          }}
                        >
                          Select Columns
                        </div>
                        {options.map((label, index) => (
                          <label
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "10px",
                              fontSize: "14px",
                              cursor: "pointer",
                            }}
                          >
                            <input type="checkbox" checked={!!selectedColumns[label]} onChange={() => toggleColumn(label)} style={{ marginRight: "8px" }} />
                            {label}
                          </label>
                        ))}
                        <button
                          onClick={() => setShowModal(false)}
                          style={{
                            marginTop: "16px",
                            width: "100%",
                            padding: "10px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: "#3D4AF5",
                            color: "#fff",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(event.target.value);
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  key={itemOffset}
                  previousLabel={<img src={lar} className="ar3" alt="previous" />}
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>

          <ToastContainer position="top-end">
            <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
              <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      </div>
    </div>
  );
};

export default ItemList;
